import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Mail, Search, BookOpen, Home, Package, Pencil, Receipt,
  FileText, Sparkles, Users, Settings, Palette, Rocket, ChevronRight, ChevronLeft, X,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SendByEmailDialog from '../../components/SendByEmailDialog';
import { toast } from 'sonner';

const ICON_MAP = {
  Home, Package, Pencil, Receipt, FileText, Sparkles, BookOpen, Mail,
  Users, Settings, Palette, Rocket,
};

/**
 * Admin Handbuch — responsives, buchähnliches Layout.
 *
 * Auflösungs-Strategie:
 *  - ≥ 1024px (Laptop/Desktop): Linke Sidebar + Inhalts-Seite
 *  - 640-1023px (Tablet):       Sticky horizontaler Kapitel-Chip-Scroller über Inhalt
 *  - <  640px  (Handy):         Identisch wie Tablet, aber kompaktere Buttons + Padding
 */
const AdminManual = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const chipScrollerRef = useRef(null);

  useEffect(() => {
    axiosInstance.get('/admin-manual/sections')
      .then(r => {
        const data = r.data || [];
        setSections(data);
        if (data.length) setActiveId(data[0].id);
      })
      .catch(() => toast.error('Handbuch konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  // Scroll the active chip into view in the horizontal scroller
  useEffect(() => {
    if (!chipScrollerRef.current || !activeId) return;
    const el = chipScrollerRef.current.querySelector(`[data-chip-id="${activeId}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return sections;
    const q = query.toLowerCase();
    return sections.filter(s =>
      s.title.toLowerCase().includes(q) ||
      (s.summary || '').toLowerCase().includes(q) ||
      (s.steps || []).some(step => step.toLowerCase().includes(q))
    );
  }, [sections, query]);

  const active = sections.find(s => s.id === activeId);
  const activeIdx = sections.indexOf(active);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin-manual/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Homeyoursoul-Admin-Handbuch.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    }
  }, []);

  const handleOpenPdfTab = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin-manual/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('PDF konnte nicht geöffnet werden');
    }
  }, []);

  const emailFiles = useMemo(() => ([{
    filename: 'Homeyoursoul-Admin-Handbuch.pdf',
    getBlob: async () => {
      const res = await axiosInstance.get('/admin-manual/pdf', { responseType: 'blob' });
      return res.data;
    },
  }]), []);

  const goPrev = () => {
    if (activeIdx > 0) setActiveId(sections[activeIdx - 1].id);
  };
  const goNext = () => {
    if (activeIdx < sections.length - 1) setActiveId(sections[activeIdx + 1].id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <p className="text-[#8B7355]">Handbuch wird geladen ...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EDE4' }} data-testid="admin-manual-page">
      {/* Top bar — sticky only on lg+ where the global admin shell collapses; on tablet/mobile it stays inline to avoid double-stacking. */}
      <div className="lg:sticky lg:top-0 z-30 bg-white/95 backdrop-blur border-b" style={{ borderColor: '#E8DCC8' }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="p-1.5 sm:p-2 text-[#8B7355] hover:text-[#5D4E3C] flex-shrink-0"
            data-testid="manual-back"
            aria-label="Zurück zum Dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <BookOpen className="h-5 w-5 hidden sm:block flex-shrink-0" style={{ color: '#C4A084' }} />
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-[#5D4E3C] leading-tight truncate">Admin-Handbuch</h1>
            <p className="text-xs text-[#8B7355] hidden md:block truncate">
              Deine Schritt-für-Schritt-Anleitung — lesen oder als Buch herunterladen.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPdfTab}
            data-testid="manual-open-pdf"
            className="hidden md:inline-flex border-[#C4A084] text-[#5D4E3C] h-8"
          >
            <FileText className="h-4 w-4 mr-1.5" />
            Vorschau
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailOpen(true)}
            data-testid="manual-send-email"
            className="border-[#C4A084] text-[#5D4E3C] h-8 px-2 sm:px-3"
          >
            <Mail className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">E-Mail</span>
          </Button>
          <Button
            size="sm"
            onClick={handleDownloadPdf}
            data-testid="manual-download-pdf"
            style={{ backgroundColor: '#C4A084' }}
            className="text-white hover:opacity-90 h-8 px-2 sm:px-3"
          >
            <Download className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
        </div>
      </div>

      {/* MOBILE / TABLET: horizontal chip scroller (below lg) — sticky to viewport top */}
      <div className="lg:hidden sticky top-0 z-20 bg-white border-b" style={{ borderColor: '#E8DCC8' }}>
        <div className="max-w-6xl mx-auto px-3 py-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowSearch(v => !v)}
            data-testid="manual-search-toggle"
            className="p-1.5 text-[#8B7355] hover:text-[#5D4E3C] flex-shrink-0"
            aria-label="Suchen"
          >
            {showSearch ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </button>
          {showSearch ? (
            <Input
              autoFocus
              placeholder="Im Handbuch suchen ..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="manual-search-mobile"
              className="h-8 text-sm border-[#E8DCC8] focus-visible:ring-[#C4A084]"
            />
          ) : (
            <div
              ref={chipScrollerRef}
              className="flex-1 flex gap-2 overflow-x-auto scrollbar-none"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
              data-testid="manual-chip-scroller"
            >
              {filtered.map((s, i) => {
                const Icon = ICON_MAP[s.icon] || BookOpen;
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-chip-id={s.id}
                    onClick={() => {
                      setActiveId(s.id);
                      document.getElementById('manual-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    data-testid={`manual-chip-${s.id}`}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap"
                    style={{
                      backgroundColor: isActive ? '#5D4E3C' : '#FAF5EE',
                      color: isActive ? '#FFFFFF' : '#5D4E3C',
                      border: `1px solid ${isActive ? '#5D4E3C' : '#E8DCC8'}`,
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{sections.indexOf(s) + 1}. {s.title.replace(/^\d+\.\s*/, '')}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <span className="text-xs text-[#8B7355] py-1.5">Keine Kapitel gefunden.</span>
              )}
            </div>
          )}
        </div>
        {/* When searching on mobile, also show filtered chapter results below */}
        {showSearch && query.trim() && (
          <div className="max-w-6xl mx-auto px-3 pb-2 flex flex-wrap gap-1.5" data-testid="manual-search-results">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#8B7355] py-1">Keine Treffer für „{query}".</p>
            ) : filtered.map(s => {
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setActiveId(s.id);
                    setShowSearch(false);
                    setQuery('');
                  }}
                  data-testid={`manual-search-result-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: isActive ? '#5D4E3C' : '#FAF5EE',
                    color: isActive ? '#FFFFFF' : '#5D4E3C',
                    border: `1px solid ${isActive ? '#5D4E3C' : '#E8DCC8'}`,
                  }}
                >
                  {s.title}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 sm:gap-6">
        {/* Sidebar (Desktop only) */}
        <aside className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white border" style={{ borderColor: '#E8DCC8' }}>
            <div className="p-3 border-b" style={{ borderColor: '#E8DCC8' }}>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A89A85]" />
                <Input
                  placeholder="Im Handbuch suchen ..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="manual-search"
                  className="pl-8 h-9 border-[#E8DCC8] focus-visible:ring-[#C4A084]"
                />
              </div>
            </div>
            <nav className="p-2 max-h-[70vh] overflow-y-auto" data-testid="manual-chapter-list">
              {filtered.length === 0 ? (
                <p className="text-xs text-[#8B7355] p-3">Keine Kapitel gefunden.</p>
              ) : filtered.map((s) => {
                const Icon = ICON_MAP[s.icon] || BookOpen;
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveId(s.id)}
                    data-testid={`manual-chapter-${s.id}`}
                    className="w-full text-left px-2.5 py-2 mb-1 flex items-start gap-2.5 transition-colors"
                    style={{
                      backgroundColor: isActive ? '#FAF5EE' : 'transparent',
                      borderLeft: `3px solid ${isActive ? '#C4A084' : 'transparent'}`,
                    }}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: isActive ? '#C4A084' : '#8B7355' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#5D4E3C] leading-tight">{s.title}</p>
                      <p className="text-xs text-[#A89A85] leading-snug mt-0.5 line-clamp-2">{s.summary}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content (book page feel) */}
        <main id="manual-content" data-testid="manual-content">
          {active ? (
            <article
              className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 shadow-sm border"
              style={{ borderColor: '#E8DCC8', minHeight: '60vh' }}
            >
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] mb-2" style={{ color: '#C4A084' }}>
                Kapitel {String(activeIdx + 1).padStart(2, '0')}
              </p>
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-3"
                style={{ color: '#5D4E3C', fontFamily: 'Georgia, "Times New Roman", serif' }}
                data-testid="manual-chapter-title"
              >
                {active.title.replace(/^\d+\.\s*/, '')}
              </h2>
              <div className="w-10 sm:w-12 h-[2px] mb-4 sm:mb-6" style={{ backgroundColor: '#C4A084' }} />
              <p
                className="text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 italic"
                style={{ color: '#6D5A47', fontFamily: 'Georgia, serif' }}
              >
                {active.intro || active.summary}
              </p>

              <h3 className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold mb-3 sm:mb-4" style={{ color: '#5D4E3C' }}>
                Schritt für Schritt
              </h3>
              <ol className="space-y-3 sm:space-y-4">
                {(active.steps || []).map((step, i) => (
                  <li key={i} className="flex gap-3 sm:gap-4" data-testid={`manual-step-${i + 1}`}>
                    <span
                      className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold rounded-full"
                      style={{ backgroundColor: '#FAF5EE', color: '#C4A084', border: '1px solid #E8DCC8' }}
                    >
                      {i + 1}
                    </span>
                    <p
                      className="flex-1 text-sm sm:text-base leading-relaxed pt-0.5 sm:pt-1 break-words"
                      style={{ color: '#3D3D3D', fontFamily: 'Georgia, serif' }}
                    >
                      {step}
                    </p>
                  </li>
                ))}
              </ol>

              {/* Prev / Next chapter navigation */}
              <div className="mt-8 sm:mt-12 pt-4 sm:pt-6 border-t flex items-center justify-between gap-3" style={{ borderColor: '#E8DCC8' }}>
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={activeIdx <= 0}
                  data-testid="manual-prev-chapter"
                  className="flex items-center gap-1.5 text-xs sm:text-sm text-[#8B7355] hover:text-[#5D4E3C] disabled:opacity-30 disabled:cursor-not-allowed min-w-0"
                >
                  <ChevronLeft className="h-4 w-4 flex-shrink-0" />
                  <span className="uppercase tracking-wider text-[10px] sm:text-xs hidden sm:inline">Zurück</span>
                  <span className="font-medium truncate">
                    {activeIdx > 0 ? sections[activeIdx - 1].title.replace(/^\d+\.\s*/, '') : ''}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={activeIdx >= sections.length - 1}
                  data-testid="manual-next-chapter"
                  className="flex items-center gap-1.5 text-xs sm:text-sm text-[#8B7355] hover:text-[#5D4E3C] disabled:opacity-30 disabled:cursor-not-allowed min-w-0 text-right"
                >
                  <span className="font-medium truncate">
                    {activeIdx < sections.length - 1 ? sections[activeIdx + 1].title.replace(/^\d+\.\s*/, '') : ''}
                  </span>
                  <span className="uppercase tracking-wider text-[10px] sm:text-xs hidden sm:inline">Weiter</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                </button>
              </div>
            </article>
          ) : (
            <div className="bg-white p-6 sm:p-10 text-center text-[#8B7355]">
              Wähle ein Kapitel aus der Liste.
            </div>
          )}
        </main>
      </div>

      <SendByEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        files={emailFiles}
        defaultSubject="Dein Admin-Handbuch · Homeyoursoul"
        defaultMessage={
          'Hallo,\n\nim Anhang findest du das aktuelle Admin-Handbuch als PDF.\n\n' +
          'Viel Freude beim Lesen!\nHomeyoursoul'
        }
      />
    </div>
  );
};

export default AdminManual;
